/**
 * Notifications (toasts).
 *
 *   g.notify(message, opts)   generic (opts.type decides the look)
 *   g.success(message, opts)  g.error(...)  g.warning(...)  g.info(...)
 *
 * Defaults live in notify.defaults (position, duration, ...) and every call
 * can override them via opts. With { nextPage: true } the notification is not
 * shown now: it is queued in sessionStorage and displayed automatically on the
 * next page load (the queue is flushed below, on DOMContentLoaded). The
 * library itself never reloads or redirects - that is the caller's job.
 *
 * Markup is generated here; styles in src/scss/_notify.scss (.genus-toast*).
 * Full option reference: docs/libs/genus.md.
 */
import { translate } from './translate.js';

const STORAGE_KEY = 'genus-notify-next';

const TYPES = {
	success: { icon: 'ri-checkbox-circle-fill' },
	error: { icon: 'ri-close-circle-fill' },
	warning: { icon: 'ri-alert-fill' },
	info: { icon: 'ri-information-fill' },
};

const POSITIONS = ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'];

export function notify(message, opts) {
	opts = { ...notify.defaults, ...opts };
	if (opts.nextPage) {
		enqueue(message, opts);
		return null;
	}
	return show(message, opts);
}

// Global defaults. Override per call via opts, or for the whole app by
// mutating this object (e.g. g.notify.defaults.position = 'top-center').
notify.defaults = {
	type: 'info',        // success | error | warning | info
	position: 'bottom-right', // top|bottom - left|center|right
	duration: 3000,      // ms until auto-hide; 0 = stays until closed
	dismissible: true,   // show the close (x) button
	title: '',           // optional bold first line
	icon: '',            // ri-* class override; '' = the type's default icon
	html: false,         // render message as HTML (only for trusted strings)
	nextPage: false,     // queue for the next page load instead of showing now
};

export const success = (message, opts) => notify(message, { ...opts, type: 'success' });
export const error = (message, opts) => notify(message, { ...opts, type: 'error' });
export const warning = (message, opts) => notify(message, { ...opts, type: 'warning' });
export const info = (message, opts) => notify(message, { ...opts, type: 'info' });

// One fixed container per position, created on demand, removed when empty.
function getContainer(position) {
	if (!POSITIONS.includes(position)) position = notify.defaults.position;
	let el = document.querySelector(`.genus-toasts[data-position="${position}"]`);
	if (!el) {
		el = document.createElement('div');
		el.className = `genus-toasts genus-toasts-${position}`;
		el.setAttribute('data-position', position);
		document.body.appendChild(el);
	}
	return el;
}

function show(message, opts) {
	const type = TYPES[opts.type] ? opts.type : 'info';

	const toast = document.createElement('div');
	toast.className = `genus-toast genus-toast-${type}`;
	toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

	const icon = document.createElement('i');
	icon.className = `genus-toast-icon ${opts.icon || TYPES[type].icon}`;
	icon.setAttribute('aria-hidden', 'true');
	toast.appendChild(icon);

	const content = document.createElement('div');
	content.className = 'genus-toast-content';
	if (opts.title) {
		const title = document.createElement('div');
		title.className = 'genus-toast-title';
		title.textContent = opts.title;
		content.appendChild(title);
	}
	const msg = document.createElement('div');
	msg.className = 'genus-toast-message';
	if (opts.html) msg.innerHTML = message;
	else msg.textContent = message;
	content.appendChild(msg);
	toast.appendChild(content);

	if (opts.dismissible) {
		const close = document.createElement('button');
		close.type = 'button';
		close.className = 'genus-toast-close';
		close.setAttribute('aria-label', translate('Close notification'));
		close.innerHTML = '<i class="ri-close-line" aria-hidden="true"></i>';
		close.addEventListener('click', () => hide(toast));
		toast.appendChild(close);
	}

	getContainer(opts.position).appendChild(toast);

	// Two frames so the browser paints the initial state first and the entry
	// transition actually runs.
	requestAnimationFrame(() => {
		requestAnimationFrame(() => toast.classList.add('show'));
	});

	// Auto-hide; hovering pauses the countdown (restarts full on leave).
	if (opts.duration > 0) {
		let timer = setTimeout(() => hide(toast), opts.duration);
		toast.addEventListener('mouseenter', () => clearTimeout(timer));
		toast.addEventListener('mouseleave', () => {
			clearTimeout(timer);
			timer = setTimeout(() => hide(toast), opts.duration);
		});
	}

	return toast;
}

function hide(toast) {
	if (!toast.parentNode) return;
	toast.classList.remove('show');
	let removed = false;
	const remove = () => {
		if (removed) return;
		removed = true;
		const parent = toast.parentNode;
		if (parent) {
			parent.removeChild(toast);
			if (!parent.children.length) parent.remove();
		}
	};
	toast.addEventListener('transitionend', remove);
	setTimeout(remove, 300); // fallback (prefers-reduced-motion has no transition)
}

// ---- nextPage queue (sessionStorage) ----------------------------------------
function readQueue() {
	try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
}

function enqueue(message, opts) {
	const list = readQueue();
	list.push({ message: String(message), opts: { ...opts, nextPage: false } });
	try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch {}
}

function flushQueue() {
	const list = readQueue();
	try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
	list.forEach((item) => notify(item.message, item.opts));
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', flushQueue);
} else {
	flushQueue();
}
